import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authClient } from '@/lib/auth-client'
import { api, isApiConfigured } from '@/lib/api'
import {
  fetchUserData,
  processQueue,
  initAutoSync,
  updateQueuedItemsWithUserId,
  setCurrentUserId,
  clearCurrentUserId,
} from '@/lib/sync'

interface AuthUser {
  id: string
  email: string
  name: string | null
}

interface UserProfile {
  avatarUrl: string | null
  displayName: string | null
  username: string | null
  bio: string | null
}

interface AuthState {
  user: AuthUser | null
  profile: UserProfile | null
  isLoading: boolean
  error: string | null
  isSyncing: boolean
  lastSyncAt: string | null

  initialize: () => Promise<void>
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>
  signUp: (email: string, password: string, username?: string) => Promise<{ success: boolean; error?: string }>
  sendMagicLink: (email: string) => Promise<{ success: boolean; error?: string }>
  signOut: () => Promise<void>
  syncData: () => Promise<void>
  fetchProfile: () => Promise<void>
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ success: boolean; error?: string }>
  uploadAvatar: (file: File) => Promise<{ success: boolean; url?: string; error?: string }>
  clearError: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      profile: null,
      isLoading: true,
      error: null,
      isSyncing: false,
      lastSyncAt: null,

      initialize: async () => {
        if (!isApiConfigured()) {
          set({ isLoading: false })
          return
        }

        try {
          const result = await authClient.getSession()
          const user = result.data?.user

          if (user) {
            const authUser: AuthUser = {
              id: user.id,
              email: user.email,
              name: user.name,
            }
            set({ user: authUser, isLoading: false })
            setCurrentUserId(user.id)
            initAutoSync()
            get().syncData()
            get().fetchProfile()
          } else {
            set({ user: null, isLoading: false })
          }
        } catch (error) {
          console.error('[Auth] Initialization error:', error)
          set({ isLoading: false })
        }
      },

      signIn: async (email, password) => {
        set({ isLoading: true, error: null })
        try {
          const result = await authClient.signIn.email({ email, password })

          if (result.error) {
            let message = result.error.message || 'Sign in failed'
            if (message.includes('Invalid')) {
              message = 'Invalid email or password. Make sure you signed up first!'
            }
            set({ isLoading: false, error: message })
            return { success: false, error: message }
          }

          const user = result.data?.user
          if (user) {
            const authUser: AuthUser = {
              id: user.id,
              email: user.email,
              name: user.name,
            }
            set({ user: authUser, isLoading: false })
            setCurrentUserId(user.id)
            get().syncData()
            get().fetchProfile()
          }

          return { success: true }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Sign in failed'
          set({ isLoading: false, error: message })
          return { success: false, error: message }
        }
      },

      signUp: async (email, password, username) => {
        set({ isLoading: true, error: null })
        try {
          const result = await authClient.signUp.email({
            email,
            password,
            name: username || email.split('@')[0],
          })

          if (result.error) {
            const message = result.error.message || 'Sign up failed'
            set({ isLoading: false, error: message })
            return { success: false, error: message }
          }

          const user = result.data?.user
          if (user) {
            const authUser: AuthUser = {
              id: user.id,
              email: user.email,
              name: user.name,
            }
            set({ user: authUser, isLoading: false })
            setCurrentUserId(user.id)
          } else {
            set({ isLoading: false })
          }

          return { success: true }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Sign up failed'
          set({ isLoading: false, error: message })
          return { success: false, error: message }
        }
      },

      sendMagicLink: async (email) => {
        set({ isLoading: true, error: null })
        try {
          const result = await authClient.signIn.magicLink({ email })
          if (result.error) {
            const message = result.error.message || 'Failed to send magic link'
            set({ isLoading: false, error: message })
            return { success: false, error: message }
          }
          set({ isLoading: false })
          return { success: true }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to send magic link'
          set({ isLoading: false, error: message })
          return { success: false, error: message }
        }
      },

      signOut: async () => {
        set({ isLoading: true })
        try {
          await authClient.signOut()
          clearCurrentUserId()
          set({
            user: null,
            profile: null,
            isLoading: false,
            error: null,
          })
        } catch (error) {
          console.error('[Auth] Sign out error:', error)
          set({ isLoading: false })
        }
      },

      syncData: async () => {
        const { user, isSyncing } = get()
        if (!user || isSyncing || !isApiConfigured()) return

        set({ isSyncing: true })

        try {
          updateQueuedItemsWithUserId(user.id)
          await processQueue()

          const remoteData = await fetchUserData(user.id)

          if (remoteData) {
            const { useUserStore } = await import('./useUserStore')
            const { useTaskStore } = await import('./useTaskStore')

            if (remoteData.profile) {
              const profile = remoteData.profile as {
                level?: number
                xp?: number
                gems?: number
                streak_days?: number
                username?: string
              }

              useUserStore.setState({
                level: profile.level ?? 1,
                xp: profile.xp ?? 0,
                gems: profile.gems ?? 100,
                streakDays: profile.streak_days ?? 0,
                username: profile.username ?? null,
              })
            }

            if (remoteData.tasks && remoteData.tasks.length > 0) {
              const localTasks = useTaskStore.getState().tasks
              const remoteTasks = remoteData.tasks.map((t) => ({
                id: t.id as string,
                userId: t.user_id as string,
                title: t.title as string,
                description: t.description as string | null,
                difficulty: t.difficulty as 'easy' | 'medium' | 'hard' | 'boss',
                category: t.category as string | null,
                xpReward: t.xp_reward as number,
                dueDate: t.due_date as string | null,
                dueTime: t.due_time as string | null,
                isRecurring: Boolean(t.is_recurring),
                recurrencePattern: t.recurrence_pattern
                  ? (typeof t.recurrence_pattern === 'string'
                    ? JSON.parse(t.recurrence_pattern as string)
                    : t.recurrence_pattern) as Record<string, unknown> | null
                  : null,
                completed: Boolean(t.completed),
                completedAt: t.completed_at as string | null,
                createdAt: t.created_at as string,
              }))

              const taskMap = new Map(localTasks.map((t) => [t.id, t]))
              for (const task of remoteTasks) {
                if (!taskMap.has(task.id)) {
                  taskMap.set(task.id, task as typeof localTasks[0])
                }
              }

              useTaskStore.setState({ tasks: Array.from(taskMap.values()) })
            }
          }

          set({ lastSyncAt: new Date().toISOString(), isSyncing: false })
        } catch (error) {
          console.error('[Auth] Sync error:', error)
          set({ isSyncing: false })
        }
      },

      fetchProfile: async () => {
        const { user } = get()
        if (!user || !isApiConfigured()) return

        try {
          const data = await api.get<Record<string, unknown>>('/api/profile')

          set({
            profile: {
              avatarUrl: (data.avatar_url as string) ?? null,
              displayName: (data.display_name as string) ?? null,
              username: (data.username as string) ?? null,
              bio: (data.bio as string) ?? null,
            },
          })
        } catch (error) {
          console.error('[Auth] Failed to fetch profile:', error)
        }
      },

      updateProfile: async (updates) => {
        const { user } = get()
        if (!user || !isApiConfigured()) {
          return { success: false, error: 'Not authenticated' }
        }

        try {
          const dbUpdates: Record<string, unknown> = {}
          if (updates.avatarUrl !== undefined) dbUpdates.avatar_url = updates.avatarUrl
          if (updates.displayName !== undefined) dbUpdates.display_name = updates.displayName
          if (updates.username !== undefined) dbUpdates.username = updates.username
          if (updates.bio !== undefined) dbUpdates.bio = updates.bio

          await api.patch('/api/profile', dbUpdates)

          set((state) => ({
            profile: state.profile
              ? { ...state.profile, ...updates }
              : {
                  avatarUrl: updates.avatarUrl ?? null,
                  displayName: updates.displayName ?? null,
                  username: updates.username ?? null,
                  bio: updates.bio ?? null,
                },
          }))

          return { success: true }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to update profile'
          console.error('[Auth] Failed to update profile:', error)
          return { success: false, error: message }
        }
      },

      uploadAvatar: async (file) => {
        const { user } = get()
        if (!user || !isApiConfigured()) {
          return { success: false, error: 'Not authenticated' }
        }

        try {
          const maxSize = 2 * 1024 * 1024
          if (file.size > maxSize) {
            return { success: false, error: 'Image must be less than 2MB' }
          }

          const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
          if (!validTypes.includes(file.type)) {
            return { success: false, error: 'Only JPEG, PNG, GIF, and WebP images are allowed' }
          }

          // Get presigned upload URL
          const { uploadUrl, publicUrl } = await api.post<{
            uploadUrl: string
            publicUrl: string
            key: string
          }>('/api/profile/avatar', { contentType: file.type })

          // Upload directly to S3
          await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type },
          })

          // Update profile with new avatar URL
          await api.patch('/api/profile', { avatar_url: publicUrl })

          set((state) => ({
            profile: state.profile
              ? { ...state.profile, avatarUrl: publicUrl }
              : { avatarUrl: publicUrl, displayName: null, username: null, bio: null },
          }))

          return { success: true, url: publicUrl }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to upload avatar'
          console.error('[Auth] Failed to upload avatar:', error)
          return { success: false, error: message }
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'waifudo-auth',
      partialize: (state) => ({
        lastSyncAt: state.lastSyncAt,
      }),
    }
  )
)
